import React, { useCallback, useEffect, useRef, useState } from 'react';
import './astral.css';

function edge(origin, angle) {
  const rad = angle * Math.PI / 180, dx = Math.sin(rad), dy = -Math.cos(rad);
  const distance = Math.min(dx > 0 ? (100-origin.x)/dx : dx < 0 ? -origin.x/dx : Infinity,
    dy > 0 ? (100-origin.y)/dy : dy < 0 ? -origin.y/dy : Infinity);
  return [origin.x + dx * distance, origin.y + dy * distance];
}

export function AstralArena({ status, countdown, players, result, startsAt, serverOffset, onAnimationDone, onContinue, layout, Ball }) {
  const [close, setClose] = useState(false);
  const cameraRef = useRef(null);
  const markerRef = useRef(null);
  const trackBall = useCallback((point, elapsed) => {
    if (!cameraRef.current || !close) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const progress = reduced ? 0 : Math.min(1, Math.max(0, (elapsed - 5600) / 900));
    const eased = progress * progress * (3 - 2 * progress);
    const scale = 1 + 1.5 * eased;
    cameraRef.current.style.transformOrigin = '50% 50%';
    cameraRef.current.style.transform = `translate(${(50-point.x)*scale*eased}%, ${(50-point.y)*scale*eased}%) scale(${scale})`;
    if (markerRef.current) {
      markerRef.current.style.left = `${point.x}%`;
      markerRef.current.style.top = `${point.y}%`;
    }
  }, [close]);
  useEffect(() => {
    setClose(status === 'finished');
    if (status !== 'playing' || !startsAt) return;
    const timer = setTimeout(() => setClose(true), Math.max(0, Date.parse(startsAt) + 5600 - Date.now() - serverOffset));
    return () => clearTimeout(timer);
  }, [status, startsAt, serverOffset]);
  const total = players.reduce((sum, player) => sum + player.stake.crystalsValue, 0);
  let cursor = layout.rotation;
  const zones = players.map(player => {
    const share = total ? player.stake.crystalsValue / total : 0;
    const start = cursor, end = start + share * 360;
    cursor = end;
    const points = [[layout.origin.x, layout.origin.y], edge(layout.origin, start)];
    // Include exact rectangle corners so the sectors cover every pixel.
    for (const corner of [[0,0],[100,0],[100,100],[0,100]]) {
      let angle = Math.atan2(corner[0]-layout.origin.x, -(corner[1]-layout.origin.y))*180/Math.PI;
      while (angle < start) angle += 360;
      if (angle < end) points.push(Object.assign(corner, { angle }));
    }
    const middle = (start+end)/2*Math.PI/180;
    const corners = points.slice(2).sort((a,b) => a.angle-b.angle);
    return { player, share, points: [...points.slice(0,2), ...corners, edge(layout.origin,end)].map(p => p.join(',')).join(' '),
      x: layout.origin.x + Math.sin(middle)*32, y: layout.origin.y - Math.cos(middle)*32 };
  });
  const winner = players.find(player => player.userId === result?.winnerUserId);
  const validPoint = Number.isFinite(result?.finalX) && Number.isFinite(result?.finalY);
  const zoom = close && validPoint;
  const map = <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">{zones.map(({ player, points }) =>
    <polygon key={player.userId} points={points} fill={player.color} stroke="#e9dcff" strokeWidth=".25" />)}</svg>;
  return <section className="_arena_1vwda_1 astral-arena" aria-label="Игровое поле Ва-банк">
    <div ref={cameraRef} className="astral-camera" style={{ transition: status === 'playing' ? 'none' : undefined, transform: zoom && status !== 'playing' ? `translate(${(50 - result.finalX)*2.5}%, ${(50 - result.finalY)*2.5}%) scale(2.5)` : 'none', transformOrigin: '50% 50%' }}>
      <div className="astral-map">{map}</div>
      <div className="astral-stars" />
      {zones.map(({ player, share, x, y }) => <div className={`astral-player ${status === 'finished' && winner?.userId === player.userId ? 'is-winner' : ''}`} key={player.userId}
        style={{ left: `${players.length === 1 ? 50 : x}%`, top: `${players.length === 1 ? 48 : y}%`, '--player-color': player.color }} title={`${player.username}: ${(share*100).toFixed(1)}%`}>
        <span>{player.avatarUrl ? <img src={player.avatarUrl} alt={player.username} /> : player.username.replace('@','').slice(0,1)}</span>
        <b>{(share*100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%</b>
      </div>)}
      {players.length < 2 && <div className="astral-orbits" />}
      {status === 'playing' && result && startsAt && <Ball seed={result.animationSeed} roll={result.roll} maxRoll={result.maxRoll} finalX={result.finalX} finalY={result.finalY} startedAt={startsAt} serverOffset={serverOffset} zoneOrigin={{ ...layout.origin, rotation: layout.rotation }} onDone={onAnimationDone} onPosition={trackBall} />}
      {status === 'finished' && validPoint && <i className="_orb_1vwda_1 astral-stopped" style={{ left: `${result.finalX}%`, top: `${result.finalY}%` }} />}
    </div>
    <div className="astral-status">{status === 'countdown' ? `СТАРТ ЧЕРЕЗ ${countdown}` : status === 'playing' ? 'РОЗЫГРЫШ' : status === 'finished' ? 'ИГРА ЗАВЕРШЕНА' : status === 'cancelled' ? 'ИГРА ОТМЕНЕНА' : status === 'settling' ? 'ПОДВОДИМ ИТОГИ' : status === 'locked' ? 'СТАВКИ ЗАКРЫТЫ' : 'ОЖИДАЕМ ИГРОКОВ'}</div>
    {status === 'waiting' && <p className="astral-wait">{players.length ? 'Ждём соперника' : 'Сделайте первую ставку'}<small>{players.length ? 'Ваша арена готова к розыгрышу' : 'Каждая ставка — своя территория'}</small></p>}
    {zoom && <div className="astral-minimap">{map}<i ref={markerRef} style={{ left: `${result.finalX}%`, top: `${result.finalY}%` }} /><small>×2.5</small></div>}
    {validPoint && ['playing','finished'].includes(status) && <button className="astral-view" onClick={() => setClose(value => !value)}>{zoom ? 'Всё поле' : 'Приблизить'}</button>}
    {status === 'finished' && winner && <div className="astral-result"><small>ПОБЕДИТЕЛЬ</small><b>{winner.username}</b>
      <strong>{(result.payoutCurrency ?? winner.stake.currency) === 'card' ? 'Выигранные карты' : `+${(result.payoutAmount ?? players.reduce((sum,p) => sum+p.stake.amount,0)).toLocaleString('ru-RU')} ${{metacoin:'M',crystal:'💎',gem:'♦',spin:'круток'}[result.payoutCurrency ?? winner.stake.currency] ?? ''}`}</strong>
      {result.wonCards?.length > 0 && <ul>{result.wonCards.map(card => <li key={card.id}>{card.name} · {card.rarity}</li>)}</ul>}
      <button onClick={onContinue}>Ещё раунд</button></div>}
  </section>;
}
